<template>
	<div>
		<transition
			enter-active-class="transition duration-200 ease-out"
			enter-from-class="opacity-0"
			enter-to-class="opacity-100"
			leave-active-class="transition duration-150 ease-in"
			leave-from-class="opacity-100"
			leave-to-class="opacity-0"
		>
			<div v-show="isOpen" class="fixed inset-0 z-50" @click="close">
				<div class="fixed inset-0 bg-black/50 dark:bg-black/80"></div>
				<div class="fixed left-1/2 top-28 w-full max-w-2xl -translate-x-1/2 p-4">
					<div
						class="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-800"
						@click.stop
					>
						<div class="flex items-center gap-4 border-b border-gray-200 p-5 dark:border-gray-700">
							<span class="fa-light fa-magnifying-glass w-5 text-center text-lg text-gray-400"></span>
							<input
								ref="searchInput"
								v-model="query"
								class="w-full border-none bg-transparent text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0 dark:text-gray-100 dark:placeholder:text-gray-500"
								placeholder="Type to search..."
								@keydown.esc="close"
								@keydown.up.prevent="navigateUp"
								@keydown.down.prevent="navigateDown"
								@keydown.enter="selectResult"
							/>
							<kbd
								class="hidden rounded bg-gray-100 px-2 py-1 text-xs text-gray-500 dark:bg-gray-700 dark:text-gray-400 sm:inline-block"
								>ESC</kbd
							>
						</div>
						<div v-if="filteredResults.length" class="px-3 py-4">
							<div class="max-h-[60vh] space-y-1 overflow-y-auto px-1">
								<div
									v-for="(result, index) in filteredResults"
									:key="result.path"
									:class="[
										'flex cursor-pointer items-center gap-4 rounded-lg px-4 py-3 transition-colors',
										index === selectedIndex
											? 'bg-gray-100 dark:bg-gray-700'
											: 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
									]"
									@click="navigateTo(result)"
								>
									<span
										:class="[
											'fa-fw w-[22.5px] text-center text-lg text-gray-400 dark:text-gray-500',
											result.iconClass,
											{ 'fa-solid': $route.path.includes(result.path) },
											{ 'fa-light': !$route.path.includes(result.path) }
										]"
									></span>
									<span class="flex-1 text-gray-900 dark:text-gray-100">{{ result.title }}</span>
									<kbd
										class="hidden rounded bg-gray-100 px-2 py-1 text-xs text-gray-500 dark:bg-gray-700 dark:text-gray-400 sm:inline-block"
										>{{ getShortcut(result.shortcut) }}</kbd
									>
								</div>
							</div>
						</div>
						<div v-else-if="query" class="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
							No results found for "{{ query }}"
						</div>
						<div v-else class="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
							Search for pages, features, and more...
						</div>
					</div>
				</div>
			</div>
		</transition>
	</div>
</template>

<script>
import { router } from '../../router'
import { getFilteredNavigation } from '../../config/navigation'

export default {
	inject: ['userRoles'],

	data() {
		return {
			isOpen: false,
			query: '',
			selectedIndex: 0,
			isMac: /Mac|iPhone|iPod|iPad/.test(navigator.userAgent)
		}
	},

	computed: {
		modifierKey() {
			return this.isMac ? '⌘' : 'Ctrl +'
		},
		pages() {
			return getFilteredNavigation(this.userRoles, this.$global)
		},
		filteredResults() {
			if (!this.query) return this.pages
			const searchQuery = this.query.toLowerCase()
			return this.pages.filter(
				(page) =>
					page.title.toLowerCase().includes(searchQuery) || page.path.toLowerCase().includes(searchQuery)
			)
		}
	},

	mounted() {
		window.addEventListener('keydown', this.handleKeydown)
	},

	beforeUnmount() {
		window.removeEventListener('keydown', this.handleKeydown)
	},

	methods: {
		getShortcut(shortcut) {
			return `${this.modifierKey} ${shortcut}`
		},
		open() {
			this.isOpen = true
			this.selectedIndex = 0
			this.query = ''
			this.$nextTick(() => {
				this.$refs.searchInput?.focus()
			})
		},
		close() {
			this.isOpen = false
		},
		navigateUp() {
			if (this.selectedIndex > 0) {
				this.selectedIndex--
			}
		},
		navigateDown() {
			if (this.selectedIndex < this.filteredResults.length - 1) {
				this.selectedIndex++
			}
		},
		navigateTo(result) {
			router.push(result.path)
			this.close()
		},
		selectResult() {
			if (this.filteredResults.length) {
				this.navigateTo(this.filteredResults[this.selectedIndex])
			}
		},
		handleKeydown(event) {
			if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
				event.preventDefault()
				this.isOpen ? this.close() : this.open()
				return
			}

			if (!this.isOpen) return

			if (event.metaKey || event.ctrlKey) {
				const key = event.key.toUpperCase()
				const matchingItem = this.pages.find((item) => item.shortcut === key)
				if (matchingItem) {
					event.preventDefault()
					this.navigateTo(matchingItem)
				}
			}
		}
	}
}
</script>
